
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import httpClient from "../../store/httpClient";
import { CalculatedOrderAuditDto } from "../../dtos/calculatedOrderAuditDto";
import OrderCalculationComponent from "@/components/OrderCalculationComponent.vue";
import { CalculatedOrderAuditArticleDto } from "@/dtos/calculatedOrderAuditArticleDto";
import DatePicker from "@/components/DatePicker.vue";
import moment from "moment";
import PriceModel from "@/dtos/priceModel";

@Component({
	components: {
		OrderCalculationComponent,
		DatePicker,
	},
})
export default class Calculations extends Vue {
	private loading: boolean = false;
	private calculations: any = [];
	private customerAudits: any = [];
	private currentOrderWorkSteps: any = [];
	private currentOrderArticleMaterials: Map<number, string> = new Map<number, string>();
	private showOrderWorkStepsDialog: boolean = false;
	private articleMaterials: Map<number, string> = new Map<number, string>();
	private searchTerm: string = "";
	private lastSearchTerm: string = "";
	private filterRegions = [];
	private fromDate: Date = new Date(moment().subtract(1, "month").format("YYYY-MM-DD"));
	private untilDate: Date = new Date();
	private groupHeaders = [
		{
			text: "Letzte Kalkulation",
			value: "lastCalculation",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Id",
			value: "customerId",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Firmenname",
			value: "companyName",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Nachname",
			value: "lastName",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Vorname",
			value: "firstName",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Land",
			value: "country",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "# Kalkulationen in Zeitraum",
			value: "totalCalculations",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Preis der letzten Kalkulation",
			value: "lastCalculationPrice",
			align: "right",
			sortable: true,
			width: "1px",
		},
	];
	private auditHeaders = [
		{
			text: "Calculation Id",
			value: "calculationId",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Date",
			value: "calculationDate",
			align: "left",
			sortable: true,
			width: "1px",
		},
		{
			text: "Benutzername",
			value: "username",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Artikel",
			value: "articles",
			align: "left",
			sortable: false,
			width: "1px",
		},
		{
			text: "Material",
			value: "materials",
			align: "left",
			sortable: false,
			width: "450px",
		},
		{
			text: "Auftragsrabatt",
			value: "orderDiscounts",
			align: "right",
			sortable: false,
			width: "50px",
		},
		{
			text: "Preis",
			value: "price",
			align: "right",
			sortable: false,
			width: "1px",
		},
	];

	private mounted() {
		this.filterRegions = this.regions;
		this.loadCalculations();
	}

	private get regions() {
		return this.$store.state.regions;
	}

	private onClickedAuditGroup(customerId: number) {
		if (this.getIndividualCustomerAudits(customerId).length <= 0) {
			this.loadCustomerCalculationAudits(customerId);
		}
	}

	private getIndividualCustomerAudits(customerId: number) {
		let audits = this.customerAudits.filter(
			(ca: CalculatedOrderAuditDto) => ca.customerId === customerId
		);
		return audits;
	}

	private getMaterialsWithWeight(articles: CalculatedOrderAuditArticleDto[]) {
		let hashMap = articles.reduce((map, article) => {
			const material = article.material;
			const weight = article.materialWeightInKilos;

			if (!map.has(material)) {
				map.set(material, 0);
			}

			map.set(material, map.get(material)! + weight);
			return map;
		}, new Map<string, number>());
		hashMap.forEach((weight, material) => {
			if (weight === 0) {
				hashMap.delete(material);
			}
		});
		return hashMap;
	}

	private get filteredCalculations() {
		let filtered = this.calculations;
		if (this.searchTerm.length >= 3) {
			filtered = filtered.filter(
				(c: any) =>
					c.customerId.toString().includes(this.searchTerm) ||
					c.companyName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
					c.givenName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
					c.name.toLowerCase().includes(this.searchTerm.toLowerCase())
			);
		}
		if (this.filterRegions.length > 0 && this.filterRegions.length < this.regions.length) {
			filtered = filtered.filter((c: any) =>
				this.filterRegions.some((f: string) =>
					f.toLowerCase().includes(c.country.toLowerCase())
				)
			);
		}

		return filtered;
	}

	private async loadCalculations() {
		try {
			this.loading = true;
			let fromString = new Date(this.fromDate).toISOString();
			let toString = new Date(this.untilDate).toISOString();
			const response = await httpClient().get(
				`Report/GetCalculatedWithoutOrder?from=${fromString}&to=${toString}`
			);
			this.calculations = response.data.calculations;
		} catch {
			console.log("Load calculations failed.");
		} finally {
			this.loading = false;
		}
	}

	private async loadCustomerCalculationAudits(customerId: number) {
		try {
			this.loading = true;
			const response = await httpClient().get(
				`Report/GetCustomerCalculationAudits?customerId=${customerId}`
			);
			response.data.forEach((d: any) => {
				if (!d.error) d.error = "";
				this.customerAudits.push(d);
			});
		} catch {
			console.log("Loading audits failed.");
		} finally {
			this.loading = false;
		}
	}

	private async showOrderCalculation(audit: CalculatedOrderAuditDto) {
		try {
			var groupBy = (xs: any, key: any) => {
				return xs.reduce((rv: any, x: any) => {
					(rv[x[key]] = rv[x[key]] || []).push(x);
					return rv;
				}, {});
			};

			this.loading = true;
			this.currentOrderWorkSteps = [];
			const stepsResponse = await httpClient().get(
				`Calculation/GetWorkSteps?orderId=0&calculationId=${audit.calculationId}`
			);
			this.currentOrderWorkSteps = Object.entries(groupBy(stepsResponse.data, "articleId"));
			this.currentOrderArticleMaterials.clear();
			this.currentOrderWorkSteps.forEach((step: any) => {
				let article = audit.articles.filter(
					(a: CalculatedOrderAuditArticleDto) => a.id.toString() === step[0]
				)[0];
				this.currentOrderArticleMaterials.set(step[0], article.material);
			});
			this.showOrderWorkStepsDialog = true;
		} catch (err) {
			console.log(
				`Error while getting the worksteps for calculation ${audit.calculationId}.`,
				err
			);
			this.$store.commit(
				"setSnackbarErrorText",
				"Arbeitsschritte konnten nicht geladen werden."
			);
		} finally {
			this.loading = false;
		}
	}

	private customSort(items: any, index: string, isDesc: boolean) {
		items.sort((a: any, b: any) => {
			if (index === "lastCalculationPrice") {
				if (isDesc) {
					return a.lastCalculationPrice - b.lastCalculationPrice;
				} else {
					return b.lastCalculationPrice - a.lastCalculationPrice;
				}
			}
			if (index === "totalCalculations") {
				if (isDesc) {
					return a.numberOfCalculations - b.numberOfCalculations;
				} else {
					return b.numberOfCalculations - a.numberOfCalculations;
				}
			}
			if (index === "lastCalculation") {
				let dateA = new Date(a.lastCalculation);
				let dateB = new Date(b.lastCalculation);
				if (isDesc) {
					return dateA.getTime() - dateB.getTime();
				} else {
					return dateB.getTime() - dateA.getTime();
				}
			}
		});
		return items;
	}

	private customerAuditSort(items: any, index: string, isDesc: boolean) {
		items.sort((a: any, b: any) => {
			if (index === "calculationDate") {
				let dateA = new Date(a.calculationDate);
				let dateB = new Date(b.calculationDate);
				if (isDesc) {
					return dateA.getTime() - dateB.getTime();
				} else {
					return dateB.getTime() - dateA.getTime();
				}
			}
		});
		return items;
	}

	/**
	 * Tells whether the audit carries a real error.
	 * Older audits store the OfferFailReason enum value "None" instead of an empty
	 * string, which does not mean that the calculation failed.
	 */
	private hasError(audit: CalculatedOrderAuditDto): boolean {
		return audit.error.length > 0 && audit.error !== "None";
	}

	private getDiscountsFromString(discountString: string | null): Array<string> {
		if (!discountString || discountString.length <= 0) {
			return new Array<string>();
		}
		return discountString.split(";");
	}

	private getOrderDiscountString(discount: string): string {
		let displayText = "";
		let model = discount.split(":")[0];
		let value = discount.split(":")[1];
		displayText += `${PriceModel.getPriceModelText(parseInt(model))}: ${value}`;
		return displayText;
	}

	private back() {
		this.$router.push({ name: "customer" });
	}

	private goToCustomer(customerId: string) {
		this.$router.push({ name: "customerDetail", params: { customerId } });
	}
}
